// Menu state helpers shared by the editor, the preview and the PDF.
import { BANNER_COLORS, CategoryData, FlavorOption } from '../types';
import { Catalog, CatalogFlavor, ProductLineId, getProductLine } from './catalog';

export const MAX_FLAVORS = 8;

export const isEmojiFlavor = (flavor: FlavorOption) => flavor.id.startsWith('emoji:');

export const toFlavorOption = (flavor: CatalogFlavor): FlavorOption => {
  const option: FlavorOption = { id: flavor.id, name: flavor.name, image: flavor.image };
  if (flavor.comingSoon) option.comingSoon = true;
  return option;
};

const sameOption = (a: FlavorOption, b: FlavorOption) =>
  a.id === b.id && a.name === b.name && a.image === b.image && !!a.comingSoon === !!b.comingSoon;

/**
 * Brings the flavors on the menu in line with the newest catalog: the editor
 * starts from the bundled snapshot and switches to the live storefront a
 * moment later. A Flow flavor that went live loses its Coming Soon tag, new
 * art and names follow Shopify, and a flavor Shopify no longer sells leaves
 * the menu. Extra (emoji) flavors are never touched. Returns `categories`
 * itself when nothing changed, so React can skip the update.
 */
export const refreshCategories = (categories: CategoryData[], catalog: Catalog): CategoryData[] => {
  let changed = false;
  const next = categories.map(category => {
    const latest = new Map((catalog[category.line] || []).map(flavor => [flavor.id, flavor]));
    let sectionChanged = false;
    const flavors = category.flavors.flatMap(flavor => {
      if (isEmojiFlavor(flavor)) return [flavor];
      const current = latest.get(flavor.id);
      if (!current) {
        sectionChanged = true;
        return [];
      }
      const option = toFlavorOption(current);
      if (sameOption(option, flavor)) return [flavor];
      sectionChanged = true;
      return [option];
    });
    if (!sectionChanged) return category;
    changed = true;
    return { ...category, flavors };
  });
  return changed ? next : categories;
};

// What a new section starts with, so the preview looks like a menu at once.
// Offices remove what they do not offer. Slugs the store does not sell are
// skipped; Coming Soon flavors and Variety are never preselected.
const STARTERS: Record<ProductLineId, string[]> = {
  'prophy-paste': ['bubblegum', 'chocolate', 'mint', 'strawberry'],
  'adult-prophy-paste': ['mint', 'cinnamon', 'bubblegum'],
  'varnish-sticks': ['bluerazz', 'bubblegum', 'marshmallow', 'strawberry'],
  'varnish-cups': ['bluerazz', 'bubblegum', 'mint', 'strawberry'],
  flow: ['bluerazz', 'cottoncandy', 'marshmallow', 'mint'],
};
const STARTER_COUNT = 4;

export interface StarterOptions {
  /**
   * Fill up with Coming Soon flavors when the line sells fewer than four. For
   * a line the office picks on purpose (Flow before launch), so the section
   * shows that line's flavors instead of staying empty.
   */
  comingSoon?: boolean;
}

export const starterFlavors = (
  catalog: Catalog,
  line: ProductLineId,
  options: StarterOptions = {},
): FlavorOption[] => {
  const all = (catalog[line] || []).filter(flavor => flavor.slug !== 'variety');
  const picked: CatalogFlavor[] = [];
  // Preferred flavors first, then the rest in catalog order.
  const pickFrom = (pool: CatalogFlavor[]) => {
    for (const slug of STARTERS[line] || []) {
      const flavor = pool.find(f => f.slug === slug);
      if (flavor && !picked.includes(flavor)) picked.push(flavor);
    }
    for (const flavor of pool) {
      if (!picked.includes(flavor)) picked.push(flavor);
    }
  };
  pickFrom(all.filter(flavor => !flavor.comingSoon));
  if (options.comingSoon) pickFrom(all.filter(flavor => flavor.comingSoon));
  return picked.slice(0, STARTER_COUNT).map(toFlavorOption);
};

let sectionCount = 0;
/** A React key that stays with its section when a section before it is removed. */
export const sectionUid = () => `section-${++sectionCount}`;

export const newSection = (catalog: Catalog, line: ProductLineId, color: string): CategoryData => ({
  uid: sectionUid(),
  line,
  name: getProductLine(line).menuLabel,
  color,
  flavors: starterFlavors(catalog, line),
});

const sameIds = (a: FlavorOption[], b: FlavorOption[]) =>
  a.length === b.length && a.every(flavor => b.some(other => other.id === flavor.id));

/**
 * Moves a section to another product line. A section still holding the
 * flavors it started with (or none at all) gets the new line's starter
 * flavors, Coming Soon ones included, so picking Flow before launch shows
 * Flow's four flavors. A section the office has edited keeps the flavors the
 * new line also sells, plus its extra (emoji) flavors. A banner still showing
 * the old line's name takes the new one; custom banner text stays.
 */
export const switchLine = (category: CategoryData, line: ProductLineId, catalog: Catalog): CategoryData => {
  if (line === category.line) return category;
  const untouched =
    category.flavors.length === 0 ||
    sameIds(category.flavors, starterFlavors(catalog, category.line)) ||
    sameIds(category.flavors, starterFlavors(catalog, category.line, { comingSoon: true }));
  const target = catalog[line] || [];
  const flavors = untouched
    ? starterFlavors(catalog, line, { comingSoon: true })
    : category.flavors.flatMap(flavor => {
        if (isEmojiFlavor(flavor)) return [flavor];
        const slug = flavor.id.split(':')[1];
        const match = target.find(t => t.slug === slug);
        return match ? [toFlavorOption(match)] : [];
      });
  const oldLabel = getProductLine(category.line).menuLabel;
  const keepName = category.name.trim() !== '' && category.name !== oldLabel;
  return {
    ...category,
    line,
    name: keepName ? category.name : getProductLine(line).menuLabel,
    flavors,
  };
};

export const starterCategories = (catalog: Catalog): CategoryData[] => [
  newSection(catalog, 'prophy-paste', BANNER_COLORS[0]),
  newSection(catalog, 'varnish-sticks', BANNER_COLORS[1]),
];

/**
 * Splits flavors into printed rows of at most `max`, as even as possible with
 * the longer rows first: 5 flavors print 3 + 2 instead of 4 + 1.
 */
export const balancedRows = <T,>(items: T[], max = 4): T[][] => {
  if (!items.length || max < 1) return [];
  const rowCount = Math.ceil(items.length / max);
  const base = Math.floor(items.length / rowCount);
  let extra = items.length % rowCount;
  const rows: T[][] = [];
  let start = 0;
  for (let row = 0; row < rowCount; row++) {
    const size = base + (extra > 0 ? 1 : 0);
    if (extra > 0) extra--;
    rows.push(items.slice(start, start + size));
    start += size;
  }
  return rows;
};

export const DEFAULT_OFFICE_NAME = 'Your Office Name';

/** "Sunny Smiles Dental" -> "flavor-menu-sunny-smiles-dental.pdf". */
export const pdfFileName = (officeName: string): string => {
  const slug = officeName
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
  if (!slug || officeName.trim() === DEFAULT_OFFICE_NAME) return 'flavor-menu.pdf';
  return `flavor-menu-${slug}.pdf`;
};

/** Whether a section prints any Coming Soon tag. */
export const showsComingSoon = (category: CategoryData): boolean =>
  !category.hideComingSoon && category.flavors.some(flavor => flavor.comingSoon);

/** Relative luminance (WCAG) of a #rgb or #rrggbb color; 0 for anything else. */
export const luminance = (hex: string): number => {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return 0;
  const full = match[1].length === 3 ? match[1].replace(/./g, ch => ch + ch) : match[1];
  const [r, g, b] = [0, 2, 4].map(i => {
    const c = parseInt(full.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Text color for a banner. Wonderful's banners print white text; on a very
 * light banner (yellow, white, pale grey) white would vanish, so it turns black.
 */
export const isLightColor = (hex: string): boolean => luminance(hex) > 0.6;

export const FIT_MIN_SIZE = 160;
export const FIT_MAX_SIZE = 600;
/** Share of the free height the sections fill, leaving air above and below. */
const FIT_FILL = 0.92;

export interface FitInput {
  /** Height the sections may use: the page minus header, title and footer. */
  available: number;
  /** Height the sections take now, drawn at `currentSize`. */
  sectionsHeight: number;
  currentSize: number;
  /** Printed flavor rows over all sections. */
  rows: number;
  /** Most flavors in one printed row. */
  widestRow: number;
  /** Flavor Spacing setting (negative overlaps the art). */
  spacing: number;
  /** Width inside the page margins. */
  pageWidth: number;
}

/**
 * Flavor art size that fills the page: every row of art grows by the same
 * amount until the sections use the free height, as long as the widest row
 * still fits across the page. Banners and names do not change with the art,
 * so they are measured once at the current size and kept.
 */
export const fitFlavorSize = (input: FitInput): number | null => {
  const { available, sectionsHeight, currentSize, rows, widestRow, spacing, pageWidth } = input;
  if (rows <= 0 || available <= 0 || !Number.isFinite(sectionsHeight)) return null;
  const fixed = Math.max(0, sectionsHeight - rows * currentSize);
  const byHeight = (available * FIT_FILL - fixed) / rows;
  const byWidth = widestRow > 0 ? pageWidth / widestRow - 4 - spacing : FIT_MAX_SIZE;
  const size = Math.min(byHeight, byWidth, FIT_MAX_SIZE);
  return Math.round(Math.max(FIT_MIN_SIZE, size));
};
